import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  Link,
  IconButton,
  Divider,
} from "@mui/material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import GitHubIcon from "@mui/icons-material/GitHub";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";

const Footer = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  const siteMap = [
    { name: t("navBarMenu.home"), path: "/" },
    { name: t("navBarMenu.services"), path: "/services" },
    { name: t("navBarMenu.expertise.cases"), path: "/cases" },
    { name: t("navBarMenu.expertise.blog"), path: "/blog" },
    { name: t("navBarMenu.expertise.activities"), path: "/activities" },
    { name: t("navBarMenu.work.experience"), path: "/experience" },
    { name: t("navBarMenu.work.projects"), path: "/projects" },
    { name: t("navBarMenu.work.technologies"), path: "/technologies" },
    { name: t("navBarMenu.contact"), path: "/contact" },
  ];

  const socialLinks = [
    {
      icon: <LinkedInIcon />,
      url: "https://www.linkedin.com/in/alexandre-sanou-data-scientist",
      label: t("socials.linkedIn"),
    },
    {
      icon: <GitHubIcon />,
      url: "https://github.com/SanouAlexandre",
      label: t("socials.github"),
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "#0B0F14",
        pt: 10,
        pb: 6,
        borderTop: "1px solid rgba(255, 255, 255, 0.05)",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={8} sx={{ mb: 8 }}>
          {/* Brand & CTA */}
          <Grid item xs={12} md={5}>
            <Typography
              variant="h4"
              sx={{
                color: "white",
                mb: 3,
                fontFamily: "Philosopher",
                fontWeight: 700,
              }}
            >
              {t("footer.cta.titlePrefix")}
              <span style={{ color: "#0FA3A3" }}>
                {t("footer.cta.titleHighlightCollaborate")}
              </span>
              {t("footer.cta.titleMiddle")}
              <span style={{ color: "#D4AF37" }}>
                {t("footer.cta.titleHighlightAmazing")}
              </span>
              {t("footer.cta.titleSuffix")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#9CA3AF",
                mb: 4,
                maxWidth: "400px",
                lineHeight: 1.6,
              }}
            >
              {t("footer.cta.description")}
            </Typography>
            <Stack direction="row" spacing={2}>
              {socialLinks.map((social, idx) => (
                <IconButton
                  key={idx}
                  href={social.url}
                  target="_blank"
                  aria-label={social.label}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.03)",
                    color: "#9CA3AF",
                    "&:hover": {
                      color: "white",
                      bgcolor: "rgba(15, 163, 163, 0.1)",
                    },
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Stack>
          </Grid>

          {/* SiteMap */}
          <Grid item xs={12} sm={6} md={3.5}>
            <Typography
              variant="h6"
              sx={{
                color: "white",
                mb: 3,
                fontWeight: 800,
                fontSize: "1rem",
                letterSpacing: 1,
              }}
            >
              {t("footer.footerMenuTitle").toUpperCase()}
            </Typography>
            <Grid container>
              {siteMap.map((item) => (
                <Grid item xs={6} key={item.name} sx={{ mb: 1.5 }}>
                  <Link
                    component={RouterLink}
                    to={item.path}
                    sx={{
                      color: "#9CA3AF",
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      "&:hover": { color: "#0FA3A3" },
                    }}
                  >
                    {item.name}
                  </Link>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Contact Direct */}
          <Grid item xs={12} sm={6} md={3.5}>
            <Typography
              variant="h6"
              sx={{
                color: "white",
                mb: 3,
                fontWeight: 800,
                fontSize: "1rem",
                letterSpacing: 1,
              }}
            >
              {t("navBarMenu.contact").toUpperCase()}
            </Typography>
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                {t("footer.contactLocation")}
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.05)", mb: 4 }} />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Typography
            variant="body2"
            sx={{ color: "#4B5563", fontSize: "0.8rem" }}
          >
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </Typography>
          <Stack direction="row" spacing={3}>
            {isAuthenticated && (
              <Link
                component={RouterLink}
                to="/admin"
                sx={{
                  color: "#4B5563",
                  fontSize: "0.8rem",
                  textDecoration: "none",
                  "&:hover": { color: "#0FA3A3" },
                }}
              >
                {t("footer.admin")}
              </Link>
            )}
            <Link
              component={RouterLink}
              to="/privacy"
              sx={{
                color: "#4B5563",
                fontSize: "0.8rem",
                textDecoration: "none",
                "&:hover": { color: "white" },
              }}
            >
              {t("footer.legal.privacy")}
            </Link>
            <Link
              component={RouterLink}
              to="/terms"
              sx={{
                color: "#4B5563",
                fontSize: "0.8rem",
                textDecoration: "none",
                "&:hover": { color: "white" },
              }}
            >
              {t("footer.legal.terms")}
            </Link>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;
